/**
 * Who is signed in, and what they may do.
 *
 * There is no token in JavaScript here and there deliberately never will be. The session is an
 * HttpOnly cookie the browser attaches by itself (see `services/api/app/websession.py`), so
 * "am I signed in?" is not a value this code holds — it is a question only the server can
 * answer, by returning an account for `GET /v1/me` or a 401.
 *
 * That inverts the usual shape and it is worth being explicit about, because the usual shape is
 * what goes wrong: an app that keeps `isLoggedIn` in a store shows a signed-in interface to
 * someone whose session was revoked on another device, and only finds out when a request fails
 * somewhere unhelpful. Asking the server is the honest version, and React Query caches it so
 * the cost is one request per session, not one per screen.
 *
 * The entitlements arrive in the same payload. Every locked control in the interface reads them
 * from here, so what the interface offers and what the API will actually allow come from a
 * single answer — a client that decides locally which features are on will eventually disagree
 * with the server, and it always disagrees in the direction that shows a paying customer a
 * feature they cannot use.
 */

import { createContext, use, type ReactNode } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiError, request } from './api';

const EntitlementsSchema = z.object({
  plan_id: z.string(),
  plan_name: z.string(),
  analyses_per_month: z.number().nullable(),
  tracks: z.number(),
  storage_bytes: z.number(),
  max_upload_bytes: z.number(),
  reference_slots: z.number(),
  compare_sources: z.number(),
  player_ab: z.boolean(),
  player_bands: z.boolean(),
  history: z.boolean(),
  detailed_measurements: z.boolean(),
  codec_preview: z.boolean(),
  album_tracks: z.number(),
  share_links: z.number(),
  comments: z.boolean(),
  exports: z.array(z.string()),
  seats: z.number(),
  granted: z.record(z.string(), z.unknown()).default({}),
});

export type Entitlements = z.infer<typeof EntitlementsSchema>;

const AccountSchema = z.object({
  id: z.string(),
  email: z.string().nullable(),
  created_at: z.string().nullable(),
  /** True before anyone has signed in: a visitor part-way through their first analysis. */
  anonymous: z.boolean(),
  entitlements: EntitlementsSchema,
  usage: z
    .object({
      tracks: z.number(),
      bytes: z.number(),
      analyses_this_month: z.number(),
    })
    .optional(),
});

export type Account = z.infer<typeof AccountSchema>;

interface SessionValue {
  account: Account | null;
  entitlements: Entitlements | null;
  isLoading: boolean;
  /** True only when the server says so — never inferred from a local flag. */
  isSignedIn: boolean;
  refresh: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

export function useAccountQuery(): UseQueryResult<Account | null> {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await request('/v1/me', AccountSchema);
      } catch (error) {
        // A 401 is an answer, not a failure: it means "nobody is signed in", which is a normal
        // state for a visitor on their first analysis. Throwing here would retry it four times
        // and then render an error page over a perfectly working product.
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: (count, error) => !(error instanceof ApiError && error.status === 401) && count < 2,
  });
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const query = useAccountQuery();
  const client = useQueryClient();

  const account = query.data ?? null;
  const value: SessionValue = {
    account,
    entitlements: account?.entitlements ?? null,
    isLoading: query.isPending,
    isSignedIn: Boolean(account && !account.anonymous),
    refresh: () => {
      void client.invalidateQueries({ queryKey: ['me'] });
    },
  };

  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): SessionValue {
  const value = use(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>.');
  return value;
}

/**
 * Whether a feature is available to this account.
 *
 * Returns `false` while the account is still loading rather than `true`. An interface that
 * flashes an unlocked control and then locks it is worse than one that unlocks a moment late:
 * the first teaches a producer that a feature they do not have exists and then takes it away.
 */
export function useEntitled(key: keyof Entitlements, minimum = 1): boolean {
  const { entitlements } = useSession();
  if (!entitlements) return false;
  const value = entitlements[key];
  if (typeof value === 'boolean') return value;
  if (value === null) return true;
  if (typeof value === 'number') return value >= minimum;
  if (Array.isArray(value)) return value.length > 0;
  return false;
}
